import {
  IDBNotesLoadedEvent,
  IDetailMountedEvent, IMainMountedEvent, ISyncTelemetryEvent, TelemetryEvent
} from '@notes/sticky-notes-layout'
import { isEdgeHubQueryParamPresent } from './lib/urlParserUtils'
import { Constants } from './shared/Constants'
import { Log } from './shared/logger/Log'
import { getServiceWorkerStatus } from './shared/Utils'

const SYNC_TELEMETRY = 'SYNC_TELEMETRY'
const MAIN_MOUNTED = '@client:MAIN_MOUNTED'
const DETAIL_MOUNTED = '@client:DETAIL_MOUNTED'
const DB_NOTES_LOADED = 'DB_NOTES_LOADED'
const TELEMETRY_DELETE_NOTE = 'DELETE_NOTE'
const TELEMETRY_NOTE_CREATED = 'NOTE_CREATED'
const TELEMETRY_NOTE_CONTENT_UPDATED = 'NOTE_CONTENT_UPDATED'
const TELEMETRY_NOTE_VIEWED = 'NOTE_VIEWED'
const TELEMETRY_TOGGLE_BOLD = 'TOGGLE_BOLD'
const TELEMETRY_TOGGLE_ITALICS = 'TOGGLE_ITALICS'
const TELEMETRY_TOGGLE_UNDERLINE = 'TOGGLE_UNDERLINE'
const TELEMETRY_TOGGLE_STRIKETHROUGH = 'TOGGLE_STRIKETHROUGH'
const TELEMETRY_TOGGLE_LIST = 'TOGGLE_LIST'
const TELEMETRY_ADD_NOTE_IMAGE = 'ADD_NOTE_IMAGE'
const TELEMETRY_COLOR_CHANGED = 'COLOR_CHANGED'
const TELEMETRY_UPDATE_SEARCH_TERM = 'UPDATE_SEARCH_TERM'
const TELEMETRY_TOGGLE_COLLAPSED_SEARCH = 'TOGGLE_COLLAPSED_SEARCH'
const TELEMETRY_SEARCH_RESULT_SELECTED = 'SEARCH_RESULT_SELECTED'

const navigationStart = performance && performance.timing
  && performance.timing.navigationStart

export interface INotesCollection {
  count: () => number
}

export class LayoutSDKTelemetryCallbacks {
  private loggedFirstSync: boolean = false
  private isEdgeHub: boolean = isEdgeHubQueryParamPresent()

  onSDKSignOut (): void {
    Log.logger.info('onSDKSignOut: the SDK has signed out')
  }

  onNoteDeleted(): void {
    Log.logger.info('onNoteDeleted: A note has been deleted')
  }

  onNotesUpdated(_: INotesCollection): void {
    // Do not do this! Notes are updated on every keystroke
  }

  private handleSyncTelemetry (event: ISyncTelemetryEvent): void {
    if (!this.loggedFirstSync) {
      const totalNotes = event.data.pageCount + event.data.syncedNotesCount
      if (event.start && event.end && navigationStart) {
        Log.logger.logReportData('FirstSync', false /* isCriticalEvent */, {
          Type: event.data.context,
          Duration: event.end - event.start,
          TTFS: event.end - navigationStart,
          Start: event.start,
          End: event.end,
        } /* payloadNonPII */, {
          TotalNotes: totalNotes,
        })
      }

      if (this.isEdgeHub) {
        const flushEvent = new CustomEvent(Constants.NEW_USER_TO_ON_ECOSYSTEM_TELEMETRY_FLUSH_EVENT, { detail: { MessageId: 'TotalSyncNotes', Value: totalNotes } })
        document.dispatchEvent(flushEvent)
      }

      this.loggedFirstSync = true
    }
  }

  private handleMainMounted (event: IMainMountedEvent): void {
    if (navigationStart) {
      Log.logger.logReportData('TTI', false /* isCriticalEvent */, {
        Duration: event.end - navigationStart,
        Start: navigationStart,
        End: event.end,
      })
    }
    this.logResourceDownloadTelemetry()
  }

  private logResourceDownloadTelemetry (): void {
    if (!performance) {
      return
    }

    const navigationTiming = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
    const resourceTimings = performance.getEntriesByType('resource') as PerformanceResourceTiming[]

    if (!navigationTiming || !resourceTimings) {
      return
    }

    const navigationTimingTelemetry = {
      name: navigationTiming.name,
      startTime: navigationTiming.startTime,
      fetchStart: navigationTiming.fetchStart,
      requestStart: navigationTiming.requestStart,
      responseEnd: navigationTiming.responseEnd,
      transferSize: navigationTiming.transferSize,
      workerStart: navigationTiming.workerStart,
    }
    const resourceTimingTelemetry = []
    for (const resource of resourceTimings) {
      if (resource.initiatorType === 'link' || resource.initiatorType === 'script' || resource.initiatorType === 'css') {
        resourceTimingTelemetry.push({
          name: resource.name,
          startTime: resource.startTime,
          fetchStart: resource.fetchStart,
          requestStart: resource.requestStart,
          responseEnd: resource.responseEnd,
          transferSize: resource.transferSize,
          workerStart: resource.workerStart,
        })
      }
    }
    const data = {
      navigationTimingTelemetry,
      resourceTimingTelemetry,
      serviceWorkerStatus: getServiceWorkerStatus(),
    }
    Log.logger.logReportData('ResourceDownloadTelemetry', false /* isCriticalEvent */, {
      ResourceDownloadTelemetry: JSON.stringify(data),
    })
  }

  private handleDetailMounted (event: IDetailMountedEvent): void {
    if (navigationStart) {
      Log.logger.logReportData('DetailRendered', false /* isCriticalEvent */, {
        Duration: event.end - navigationStart,
        Start: navigationStart,
        End: event.end,
      })
    }
  }

  private handleDBLoaded (event: IDBNotesLoadedEvent): void {
    Log.logger.logReportData('NotesLoadedFromDB', false /* isCriticalEvent */, {
      Duration: event.end - event.start,
      Start: event.start,
      End: event.end,
      TotalNotes: event.data.totalNotesCount,
    })

    if (this.isEdgeHub) {
      const flushEvent = new CustomEvent(Constants.NEW_USER_TO_ON_ECOSYSTEM_TELEMETRY_FLUSH_EVENT, { detail: { MessageId: 'TotalDbNotes', Value: event.data.totalNotesCount } })
      document.dispatchEvent(flushEvent)
    }
  }

  // tslint:disable-next-line:max-func-body-length
  onTelemetry = (event: TelemetryEvent) => {
    // Ideally, the Sticky Notes Web SDK should pass event name, but currently it does not.
    // We try to use the same event name as in the Web SDK, so we translate each event.type to
    // a corresponding event name used in logReportData in the Web SDK.
    // See all event names <https://office.visualstudio.com/OneNote/_git/sticky-notes-web-sdk?path=%2Fpackages%2Fsticky-notes-sdk-ts%2Fsrc%2Flib%2Flogging%2FEventName.ts&version=GBmaster>
    switch (event.type) {
      case SYNC_TELEMETRY:
        this.handleSyncTelemetry(event)
        break
      case MAIN_MOUNTED:
        this.handleMainMounted(event)
        break
      case DETAIL_MOUNTED:
        this.handleDetailMounted(event)
        break
      case DB_NOTES_LOADED:
        this.handleDBLoaded(event)
        break
      case TELEMETRY_DELETE_NOTE:
        Log.logger.logReportData(Constants.sdkTelemetryEventName, false /* isCriticalEvent */, { telemetryEvent: 'NoteDeleted' })
        break
      case TELEMETRY_NOTE_CREATED:
        Log.logger.logReportData(Constants.sdkTelemetryEventName, false /* isCriticalEvent */, { telemetryEvent: 'NoteCreated' })
        break
      case TELEMETRY_NOTE_VIEWED:
        Log.logger.logReportData(Constants.sdkTelemetryEventName, false /* isCriticalEvent */, { telemetryEvent: 'NoteViewed' })
        break
      case TELEMETRY_NOTE_CONTENT_UPDATED:
        Log.logger.logReportData(Constants.sdkTelemetryEventName, false /* isCriticalEvent */, { telemetryEvent: 'NoteContentUpdated' })
        break
      case TELEMETRY_TOGGLE_BOLD:
        Log.logger.logReportData(Constants.sdkTelemetryEventName, false /* isCriticalEvent */, { telemetryEvent: 'NoteInlineStyleToggled', styleType: 'Bold' })
        break
      case TELEMETRY_TOGGLE_ITALICS:
        Log.logger.logReportData(Constants.sdkTelemetryEventName, false /* isCriticalEvent */, { telemetryEvent: 'NoteInlineStyleToggled', styleType: 'Italic' })
        break
      case TELEMETRY_TOGGLE_UNDERLINE:
        Log.logger.logReportData(Constants.sdkTelemetryEventName, false /* isCriticalEvent */, { telemetryEvent: 'NoteInlineStyleToggled', styleType: 'Underline' })
        break
      case TELEMETRY_TOGGLE_STRIKETHROUGH:
        Log.logger.logReportData(Constants.sdkTelemetryEventName, false /* isCriticalEvent */, { telemetryEvent: 'NoteInlineStyleToggled', styleType: 'Strikethrough' })
        break
      case TELEMETRY_TOGGLE_LIST:
        Log.logger.logReportData(Constants.sdkTelemetryEventName, false /* isCriticalEvent */, { telemetryEvent: 'NoteBlockStyleToggled', styleType: 'Bullet' })
        break
      case TELEMETRY_ADD_NOTE_IMAGE:
        Log.logger.logReportData(Constants.sdkTelemetryEventName, false /* isCriticalEvent */, { telemetryEvent: 'ImageAddedToNote' })
        break
      case TELEMETRY_COLOR_CHANGED:
        Log.logger.logReportData(Constants.sdkTelemetryEventName, false /* isCriticalEvent */, { telemetryEvent: 'ColorChanged' })
        break
      case TELEMETRY_UPDATE_SEARCH_TERM:
        Log.logger.logReportData(Constants.sdkTelemetryEventName, false /* isCriticalEvent */, { telemetryEvent: 'SearchInitiated' })
        break
      case TELEMETRY_TOGGLE_COLLAPSED_SEARCH:
        Log.logger.logReportData(Constants.sdkTelemetryEventName, false /* isCriticalEvent */, { telemetryEvent: 'SearchOpened' })
        break
      case TELEMETRY_SEARCH_RESULT_SELECTED:
        Log.logger.logReportData(Constants.sdkTelemetryEventName, false /* isCriticalEvent */, { telemetryEvent: 'SearchResultSelected' })
        break
      default:
        Log.logger.info('onTelemetry: ' + JSON.stringify(event))
        break
    }
  }
}
